import React, { useMemo } from 'react'
import {
  GetAvailableTestTagsSubscription,
  useGetAvailableShippingTestTagsSubscription,
} from '../generated/urql.client'
import { TailSpin } from 'react-loader-spinner'
import { Blue300 } from '../lib/colors'
import { Link, useLocation } from 'react-router-dom'
import { useParams } from 'react-router'
import { useI18n } from '../provider/i18n'
import { useAtomValue } from 'jotai'
import { availableTestTagsSubscriptionAtom } from '../store/subscription.atoms'

const TagPill: React.FC<{
  tag: GetAvailableTestTagsSubscription['available_test_tag'][number]['Tag']
  isSelected?: boolean
  to: string
}> = ({ tag, isSelected, to }) => (
  <div className="inline-flex max-w-full">
    <Link
      to={to}
      title={tag.tag || '-'}
      className={`${
        isSelected
          ? 'bg-blue-700 text-white hover:text-blue-300'
          : 'bg-blue-300 text-blue-900 hover:text-blue-700'
      } block rounded-2xl py-1.5 px-3.5 font-bold no-underline overflow-hidden overflow-ellipsis whitespace-nowrap`}
    >
      {tag.tag || '-'}
    </Link>
  </div>
)

export const TestCategories = () => {
  const location = useLocation()
  const i18n = useI18n()
  const params = useParams()
  const [{ data: availableTestTagsData, fetching: nurseTagsFetching }] =
    useAtomValue(availableTestTagsSubscriptionAtom)

  const isShippingCatalog = location.pathname.startsWith('/shipping-tests')

  const [{ data: shippingTagsData, fetching: shippingTagsFetching }] =
    useGetAvailableShippingTestTagsSubscription({
      pause: !isShippingCatalog,
      variables: { limit: 500 },
    })

  const selectedTagId = useMemo(() => {
    if (
      (location.pathname.includes('/tests/tag/') ||
        location.pathname.includes('/shipping-tests/tag/')) &&
      params.tagId
    ) {
      return params.tagId
    }

    if (location.state?.tagId) {
      return location.state.tagId
    }

    return
  }, [location, params])

  const availableTestTags = useMemo(() => {
    if (isShippingCatalog) {
      return shippingTagsData?.available_shipping_test_tag || []
    }
    return availableTestTagsData?.available_test_tag || []
  }, [
    isShippingCatalog,
    shippingTagsData?.available_shipping_test_tag,
    availableTestTagsData?.available_test_tag,
  ])

  const fetching = isShippingCatalog ? shippingTagsFetching : nurseTagsFetching

  return (
    <div className="flex justify-end">
      <div className="w-full 2xl:w-[200px]">
        <div className="flex items-center gap-1 mb-5">
          <p className="text-blue-700">
            {i18n.t('components.testCategories.title')}
          </p>
          {fetching && <TailSpin color={Blue300} height={16} width={16} />}
        </div>

        <ul className="hidden md:flex md:flex-col md:gap-2">
          {availableTestTags.map(({ Tag: tag }) => (
            <li key={tag.id}>
              <TagPill
                tag={tag}
                isSelected={tag.id === selectedTagId}
                to={
                  isShippingCatalog
                    ? `/shipping-tests/tag/${encodeURIComponent(tag.id)}`
                    : `/tests/tag/${encodeURIComponent(tag.id)}`
                }
              />
            </li>
          ))}
        </ul>

        <div className="flex md:hidden flex-col gap-2 overflow-x-scroll p-5 -m-5">
          <ul className="flex gap-2">
            {availableTestTags
              .filter((_, i) => i % 3 === 0)
              .map(({ Tag: tag }) => (
                <li key={tag.id}>
                  <TagPill
                    tag={tag}
                    isSelected={tag.id === selectedTagId}
                    to={
                      isShippingCatalog
                        ? `/shipping-tests/tag/${encodeURIComponent(tag.id)}`
                        : `/tests/tag/${encodeURIComponent(tag.id)}`
                    }
                  />
                </li>
              ))}
          </ul>{' '}
          <ul className="flex gap-2">
            {availableTestTags
              .filter((_, i) => i % 3 === 1)
              .map(({ Tag: tag }) => (
                <li key={tag.id}>
                  <TagPill
                    tag={tag}
                    isSelected={tag.id === selectedTagId}
                    to={
                      isShippingCatalog
                        ? `/shipping-tests/tag/${encodeURIComponent(tag.id)}`
                        : `/tests/tag/${encodeURIComponent(tag.id)}`
                    }
                  />
                </li>
              ))}
          </ul>
          <ul className="flex gap-2">
            {availableTestTags
              .filter((_, i) => i % 3 === 2)
              .map(({ Tag: tag }) => (
                <li key={tag.id}>
                  <TagPill
                    tag={tag}
                    isSelected={tag.id === selectedTagId}
                    to={
                      isShippingCatalog
                        ? `/shipping-tests/tag/${encodeURIComponent(tag.id)}`
                        : `/tests/tag/${encodeURIComponent(tag.id)}`
                    }
                  />
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
